import { SummaryRow, SummaryView } from '@salecycle/ui-component-library';
import moment from 'moment';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RulesetSplitTest } from 'entities/rulesetSplitTest';
import { Template } from 'entities/template';
import { Actions as RouterActions, Selectors as RouterSelectors } from 'router/';
import { Selectors as TemplateSelectors } from 'sections/assets/state/templates';
import { Selectors as SplitTestSelectors } from 'sections/campaigns/state/rulesetSplitTests';
import { State } from 'store/';
import { createUrl } from '.';
import { NewWizardSplitTestTemporaryId } from '../constants';

interface StateProps {
  rulesetSplitTest: RulesetSplitTest;
  templates: { [templateId: string]: Template };
  siteId: string;
  rulesetSplitTestId: string;
}

interface DispatchProps {
  push: (path: string) => void;
}

export type Props = StateProps & DispatchProps;

export const SplitTestSummaryPage: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation('campaigns');
  const from = moment(props.rulesetSplitTest.startTime);
  const to = moment(props.rulesetSplitTest.endTime);
  const durationInDays = to.diff(from, 'days');
  const formatString = 'Do MMMM YYYY';

  const redirectToSettings = () => props.push(createUrl(props.siteId, props.rulesetSplitTestId, 'settings'));
  const redirectToVariantChoice = () => props.push(createUrl(props.siteId, props.rulesetSplitTestId, 'variant-choice'));
  return (
    <SummaryView
      heading={t('campaigns:splitTestWizard.summary.viewHeading', {
        testName: props.rulesetSplitTest.name,
      })}
      subHeading={t('campaigns:splitTestWizard.summary.viewSubHeading')}
    >
      <SummaryRow
        heading={t('campaigns:splitTestWizard.summary.testNameHeading')}
        description={t('campaigns:splitTestWizard.summary.testNameDescription', {
          testName: props.rulesetSplitTest.name,
        })}
        buttonText={t('campaigns:splitTestWizard.summary.edit')}
        onClick={redirectToSettings}
      />
      <SummaryRow
        heading={t('campaigns:splitTestWizard.summary.datesHeading')}
        description={t('campaigns:splitTestWizard.summary.datesDescription', {
          duration: durationInDays,
          from: from.format(formatString),
          to: to.format(formatString),
        })}
        buttonText={t('campaigns:splitTestWizard.summary.edit')}
        onClick={redirectToSettings}
      />
      {props.rulesetSplitTest.variants.map((variant, index) => (
        <SummaryRow
          key={`variant-${index}`}
          heading={variant.name}
          description={t('campaigns:splitTestWizard.summary.variantDescription', {
            templateName: props.templates[variant.templateId] && props.templates[variant.templateId].name,
            weight: variant.weight,
          })}
          buttonText={t('campaigns:splitTestWizard.summary.edit')}
          onClick={redirectToVariantChoice}
        />
      ))}
      <SummaryRow
        heading={t('campaigns:splitTestWizard.summary.contentHeading')}
        description={t('campaigns:splitTestWizard.summary.contentDescription', {
          contentType: props.rulesetSplitTest.type,
        })}
        buttonText={t('campaigns:splitTestWizard.summary.edit')}
        onClick={redirectToSettings}
      />
      <SummaryRow
        heading={t('campaigns:splitTestWizard.summary.objectiveHeading')}
        description={t('campaigns:splitTestWizard.summary.objectiveDescription', {
          objective: props.rulesetSplitTest.objective,
        })}
        buttonText={t('campaigns:splitTestWizard.summary.edit')}
        onClick={redirectToSettings}
      />
    </SummaryView>
  );
};

export const mapStateToProps = (state: State): StateProps => {
  const rulesetSplitTestId = RouterSelectors.getSplitTestId(state) || NewWizardSplitTestTemporaryId;
  return {
    rulesetSplitTest: SplitTestSelectors.getUpdatedEntity(state, rulesetSplitTestId),
    templates: TemplateSelectors.getEntityMap(state),
    siteId: RouterSelectors.getSiteId(state),
    rulesetSplitTestId,
  };
};

export const actionCreators: DispatchProps = {
  push: RouterActions.push,
};

export default connect(mapStateToProps, actionCreators)(SplitTestSummaryPage);
